import { defineComponent as _defineComponent } from 'vue'
import { computed, ref, watch } from "vue";
  import { useStore } from "vuex";
  import { useToast } from "vue-toastification";
  import ToggleButton from "@/components/ui/ToggleButton.vue";
  import { BuilderInterfaceWithHouseDesigns } from "@/types/BuilderInterface";
  import { LotDocument, LotInterface } from "@/types/LotInterface";
  import { EstateInterface } from "@/types/EstateInterface";
  import {
    acceptedBuilderDocumentTypes,
    deleteBuilderDocument,
    getBuilderPayload,
    getDocumentName,
    getEstateLandownerIds as getEstateLandownerIdsForEstate,
    getId,
    getSelectedBuilderId,
    openBuilderDocument,
    uploadBuilderDocument,
  } from "@/helpers/lot/homeAndLand";
  import { formatDate } from "@/helpers/date";
  import DropDown from "@/components/ui/dropdown/DropDown.vue";

  interface PackageLot extends LotInterface {
    selectedBuilderId: string;
  }

  
export default /*@__PURE__*/_defineComponent({
  __name: 'LotHomeAndLand',
  props: {
    lot: {},
    estate: {}
  },
  emits: ["updateLot"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

  const props = __props;
  const emit = __emit;

  const store = useStore();
  const toast = useToast();
  const builders = ref<BuilderInterfaceWithHouseDesigns[]>([]);
  const localLot = ref<PackageLot | null>(null);
  const loading = ref(false);
  const saving = ref(false);
  const uploading = ref(false);
  const deletingDocumentKey = ref("");
  const acceptedDocumentTypes = acceptedBuilderDocumentTypes;

  const estateId = computed(() => getEstateId());
  const builderDocuments = computed(
    () => localLot.value?.builderDocuments ?? []
  );
  const selectedBuilder = computed(() =>
    builders.value.find(
      (builder) => builder._id === localLot.value?.selectedBuilderId
    )
  );
  const selectedBuilderInitials = computed(() => {
    if (!selectedBuilder.value?.name) return "HL";

    return selectedBuilder.value.name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase();
  });
  const canUploadDocuments = computed(() => {
    return Boolean(
      localLot.value?.isBuildersEnabled && localLot.value?.selectedBuilderId
    );
  });

  watch(
    () => props.lot,
    (lot) => {
      localLot.value = lot
        ? {
            ...lot,
            selectedBuilderId: getSelectedBuilderId(lot),
          }
        : null;
    },
    { immediate: true }
  );

  watch(
    () => props.estate,
    () => {
      loadBuilders();
    },
    { immediate: true }
  );

  function toggleHomeAndLand(isEnabled: boolean) {
    if (!localLot.value) return;

    localLot.value.isBuildersEnabled = isEnabled;

    if (!isEnabled) {
      localLot.value.selectedBuilderId = "";
      localLot.value.builders = [];
    }
  }

  function selectBuilder(builder: BuilderInterfaceWithHouseDesigns) {
    if (!localLot.value) return;

    localLot.value.selectedBuilderId = builder._id;
  }

  async function saveLot() {
    if (!localLot.value) return;

    if (localLot.value.isBuildersEnabled && !localLot.value.selectedBuilderId) {
      toast.error("Select a builder before enabling H&L for this lot");
      return;
    }

    saving.value = true;

    try {
      const data = {
        isBuildersEnabled: localLot.value.isBuildersEnabled,
        builders: localLot.value.isBuildersEnabled
          ? [
              getBuilderPayload(
                builders.value,
                localLot.value.selectedBuilderId
              ),
            ]
          : [],
      };

      await store.dispatch("lot/updateLot", { id: localLot.value._id, data });
      toast.success(`Lot ${localLot.value.title} updated`);
      emit("updateLot");
    } catch {
      toast.error(`Failed to update lot ${localLot.value.title}`);
    } finally {
      saving.value = false;
    }
  }

  async function uploadDocument(event: Event) {
    if (!localLot.value) return;

    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    input.value = "";

    if (!file) return;

    if (!canUploadDocuments.value) {
      toast.error("Enable H&L and select a builder before uploading documents");
      return;
    }

    uploading.value = true;

    try {
      const document = await uploadBuilderDocument(
        store,
        file,
        localLot.value._id,
        estateId.value
      );

      if (!document) return;

      toast.success("Document uploaded");
      emit("updateLot");
    } catch {
      toast.error("Failed to upload document");
    } finally {
      uploading.value = false;
    }
  }

  async function deleteDocument(fileName: string, index: number) {
    if (!localLot.value) return;

    deletingDocumentKey.value = fileName;

    try {
      await deleteBuilderDocument(store, localLot.value._id, fileName);

      localLot.value.builderDocuments?.splice(index, 1);
      toast.success("Document deleted");
      emit("updateLot");
    } catch {
      toast.error("Failed to delete document");
    } finally {
      deletingDocumentKey.value = "";
    }
  }

  async function loadBuilders() {
    const landownerIds = getEstateLandownerIds();

    if (!landownerIds.length) {
      builders.value = [];
      return;
    }

    loading.value = true;

    try {
      const response = await store.dispatch("builder/getAllBuilderWithHouses", {
        landownerIds,
      });

      builders.value = response.data;
    } catch {
      builders.value = [];
      toast.error("Failed to load builders");
    } finally {
      loading.value = false;
    }
  }

  function getEstateId() {
    return (
      getId(localLot.value?.estateId) ||
      props.estate?._id ||
      store.state.user.selectedEstateId
    );
  }

  function getEstateLandownerIds() {
    const estate =
      props.estate ||
      store.state.user.landownerEstates?.find(
        (item: EstateInterface) => item._id === estateId.value
      );

    return getEstateLandownerIdsForEstate(estate);
  }

  function openDocument(document: LotDocument) {
    openBuilderDocument(document);
  }

const __returned__ = { props, emit, store, toast, builders, localLot, loading, saving, uploading, deletingDocumentKey, acceptedDocumentTypes, estateId, builderDocuments, selectedBuilder, selectedBuilderInitials, canUploadDocuments, toggleHomeAndLand, selectBuilder, saveLot, uploadDocument, deleteDocument, loadBuilders, getEstateId, getEstateLandownerIds, openDocument, computed, ref, watch, get useStore() { return useStore }, get useToast() { return useToast }, ToggleButton, get BuilderInterfaceWithHouseDesigns() { return BuilderInterfaceWithHouseDesigns }, get LotDocument() { return LotDocument }, get LotInterface() { return LotInterface }, get EstateInterface() { return EstateInterface }, get acceptedBuilderDocumentTypes() { return acceptedBuilderDocumentTypes }, get deleteBuilderDocument() { return deleteBuilderDocument }, get getBuilderPayload() { return getBuilderPayload }, get getDocumentName() { return getDocumentName }, get getEstateLandownerIdsForEstate() { return getEstateLandownerIdsForEstate }, get getId() { return getId }, get getSelectedBuilderId() { return getSelectedBuilderId }, get openBuilderDocument() { return openBuilderDocument }, get uploadBuilderDocument() { return uploadBuilderDocument }, get formatDate() { return formatDate }, DropDown }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})